/**
 * SubscriptionSuccess page component for post-payment confirmation.
 * 
 * This page is displayed after a successful subscription purchase through Stripe.
 * It confirms the subscription and provides next steps for the user.
 * 
 * Key Features:
 * - Displays success message after payment
 * - Shows subscription details
 * - Provides links to start using the service
 * 
 * File Dependencies:
 * This file uses: React, Material-UI, react-router-dom, utils/api
 * This file is used by: App.js routing
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fetchSubscriptionStatus } from '../utils/api';
import api from '../utils/api';
import analytics from '../services/analytics';
import { useAuth } from '../context/AuthContext';

function SubscriptionSuccess() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      
      // First, try to sync with Stripe (in case webhooks failed)
      try {
        console.log('Attempting to sync subscription status with Stripe...');
        const syncResponse = await api.post('/subscriptions/sync-status');
        console.log('Sync response:', syncResponse.data);
      } catch (syncErr) {
        console.log('Sync failed (might not have subscription yet):', syncErr);
      }
      
      // Then fetch the current status
      const response = await fetchSubscriptionStatus();
      setSubscriptionStatus(response.data);
      
      // Track successful subscription
      if (response.data?.has_active_subscription) {
        // Get plan details from URL params (if passed) or from status
        const urlParams = new URLSearchParams(window.location.search);
        const planId = urlParams.get('plan_id');
        
        // Check if user is alpha tester based on message
        const isAlphaTester = user?.is_alpha_tester || 
                             response.data.message === "Alpha tester with extended trial privileges";
        
        analytics.trackSubscriptionCheckoutCompleted(
          planId || response.data.plan_id || 'pro_monthly', // Default to pro_monthly if unknown
          response.data.plan_name || 'Pro Monthly', // Default plan name
          response.data.price_cents || 1699, // Default price in cents
          {
            exam_allowance: response.data.exam_allowance,
            is_alpha_tester: isAlphaTester,
            subscription_status: response.data.subscription_status,
            user_role: user?.role,
            user_id: user?.id
          }
        );
        
        // Track alpha discount if applied
        if (isAlphaTester && response.data.price_cents) {
          // Calculate original price (assuming 90% discount)
          const discountedPrice = response.data.price_cents;
          const originalPrice = Math.round(discountedPrice / 0.1); // 90% off means price is 10% of original
          
          analytics.trackAlphaDiscountApplied(
            'ALPHA90',
            90,
            response.data.plan_id || 'pro_monthly',
            response.data.plan_name || 'Pro Monthly',
            originalPrice,
            discountedPrice
          );
        }
      }
    } catch (err) {
      console.error('Error loading subscription status:', err);
      setError('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        
        <Typography variant="h4" component="h1" gutterBottom>
          Thanks for subscribing!
        </Typography>
        
        <Typography variant="body1" paragraph sx={{ mb: 3 }}>
          Your success journey starts now.
        </Typography>
        
        {subscriptionStatus && subscriptionStatus.has_active_subscription && (
          <Alert severity="success" sx={{ mb: 3 }}>
            You have {subscriptionStatus.allowance_remaining === null ? 'unlimited' : subscriptionStatus.allowance_remaining}{' '}
            exam{subscriptionStatus.allowance_remaining !== 1 ? 's' : ''} available this month.
          </Alert>
        )}
        
        {error && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/generate')}
          >
            Generate an Exam
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default SubscriptionSuccess;